/**
 * MissionBoard — sticky widget (right-bottom) z 10-12 misjami onboardingu.
 *
 * Pokazuje progress + active mission + "Pokaż jak". Każda misja ma:
 *   - title, description, benefit (oszczędzisz X)
 *   - "Pokaż jak" action (przekierowanie + highlight)
 *   - auto-detect (gdy user wykona, mission auto-complete)
 *
 * State synchronized z backend (/api/tutorial/state).
 * LocalStorage cache dla szybkiego load + offline.
 *
 * Collapsible: pełen panel ↔ mini pill ("3/10 ✓").
 * Dismiss permanently: button → POST /tutorial/dismiss.
 *
 * Auto-completion (called from page components via tutorialActions helper):
 *   - browse_tenders: when user navigates to /tenders
 *   - analyze_tender: when POST /api/tenders/{id}/analyze succeeds
 *   - extract_boq: when BoQ items exist for demo_opp_id
 *   - select_suppliers: when RFQ shortlist has ≥3 suppliers
 *   - send_rfq: when RFQ thread status != "drafted"
 *   - view_quotes: when user opens quotes view
 *   - check_verdict: when profitability endpoint hit
 *   - generate_offer: when offer created
 *   - generate_jedz: when JEDZ XML generated
 *   - mark_submitted: when /submit endpoint called
 *   - record_outcome: when AwardOutcomeDialog submits
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { toast } from 'sonner';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import {
  Target, Check, ChevronUp, ChevronDown, X, Sparkles, Lock,
  Lightbulb, FileText, Truck, Send, TrendingUp, Award, Clock,
  Layers, MailCheck, ListChecks
} from 'lucide-react';
import { api } from '../../lib/api';

const t = (lang, pl, en) => (lang === 'pl' ? pl : en);

// Mission definitions — must match backend MISSION_KEYS
const MISSIONS = [
  {
    key: 'browse_tenders',
    icon: Layers,
    title_pl: 'Otwórz listę przetargów',
    title_en: 'Open tenders list',
    desc_pl: 'Zobacz 800+ aktualnych ogłoszeń BZP+TED z match score',
    desc_en: 'See 800+ live tenders BZP+TED with match score',
    benefit_pl: 'Cała Polska + UE w jednym widoku',
    benefit_en: 'All Poland + EU in one view',
    action_route: '/tenders',
    estimated_min: 1,
  },
  {
    key: 'analyze_tender',
    icon: FileText,
    title_pl: 'Przeanalizuj pierwszy SWZ',
    title_en: 'Analyze first SWZ',
    desc_pl: 'AI wyciągnie wadium, termin, kryteria, red flags',
    desc_en: 'AI extracts deposit, deadline, criteria, red flags',
    benefit_pl: 'Oszczędzisz 2h przy 80-stronicowym SWZ',
    benefit_en: 'Save 2h on 80-page SWZ',
    action_route: 'demo',  // resolves to demo_opp_id from state
    estimated_min: 2,
  },
  {
    key: 'extract_boq',
    icon: ListChecks,
    title_pl: 'Wyciągnij BoQ z SWZ',
    title_en: 'Extract BoQ from SWZ',
    desc_pl: 'Automatyczna lista materiałów: 1200 opraw LED 60W, DALI 2.0',
    desc_en: 'Auto material list: 1200 LED 60W lamps, DALI 2.0',
    benefit_pl: 'Brak ręcznego przepisywania Excelu',
    benefit_en: 'No manual Excel transcribing',
    action_route: 'demo/profit-room',
    estimated_min: 1,
  },
  {
    key: 'select_suppliers',
    icon: Truck,
    title_pl: 'Wybierz dostawców do RFQ',
    title_en: 'Pick suppliers for RFQ',
    desc_pl: 'Top 5 z 70 firm w bazie — auto-sorted po historical fit',
    desc_en: 'Top 5 from 70 KB suppliers — auto-sorted by historical fit',
    benefit_pl: 'Z SPHINX intel: kapitał, sankcje, financial score',
    benefit_en: 'With SPHINX intel: capital, sanctions, financial score',
    action_route: 'demo/profit-room',
    estimated_min: 2,
  },
  {
    key: 'send_rfq',
    icon: Send,
    title_pl: 'Wyślij RFQ do dostawców',
    title_en: 'Send RFQ to suppliers',
    desc_pl: 'AI generuje email + PDF, wysłane z ada@maskil.pl',
    desc_en: 'AI generates email + PDF, sent from ada@maskil.pl',
    benefit_pl: 'Reply-to → zapytania@ → auto-parsing quote response',
    benefit_en: 'Reply-to → zapytania@ → auto-parse quote response',
    action_route: 'demo/profit-room',
    estimated_min: 2,
  },
  {
    key: 'view_quotes',
    icon: TrendingUp,
    title_pl: 'Zobacz quoty od dostawców',
    title_en: 'View supplier quotes',
    desc_pl: 'Demo ma 3 quotes — porównaj ceny, terminy, gwarancje',
    desc_en: 'Demo has 3 quotes — compare prices, delivery, warranty',
    benefit_pl: 'Average ważone — top-3 vs single supplier',
    benefit_en: 'Weighted average — top-3 vs single supplier',
    action_route: 'demo/profit-room',
    estimated_min: 2,
  },
  {
    key: 'check_verdict',
    icon: Target,
    title_pl: 'Sprawdź verdict + sensitivity',
    title_en: 'Check verdict + sensitivity',
    desc_pl: 'Adam mówi BID/PASS. Suwak: co przy -5/-10/-15% cenie?',
    desc_en: 'Adam says BID/PASS. Slider: what at -5/-10/-15% price?',
    benefit_pl: 'Decyzja data-driven, nie czuju',
    benefit_en: 'Data-driven decision, not gut',
    action_route: 'demo/profit-room',
    estimated_min: 2,
  },
  {
    key: 'generate_offer',
    icon: FileText,
    title_pl: 'Wygeneruj ofertę PDF',
    title_en: 'Generate offer PDF',
    desc_pl: 'Pełna oferta 850k PLN, 8 pozycji, gotowa do złożenia',
    desc_en: 'Full offer 850k PLN, 8 line items, ready to submit',
    benefit_pl: 'Zamiast Worda i kopiowania ręcznego',
    benefit_en: 'Instead of Word and manual copy-paste',
    action_route: 'demo',
    estimated_min: 1,
  },
  {
    key: 'generate_jedz',
    icon: Lock,
    title_pl: 'Wygeneruj JEDZ XML',
    title_en: 'Generate JEDZ XML',
    desc_pl: 'EU-compliant ESPD-1.0.2 XML + checklist + oświadczenia',
    desc_en: 'EU-compliant ESPD-1.0.2 XML + checklist + declarations',
    benefit_pl: 'JEDZ ręcznie = 30 min, tu w 10 sekund',
    benefit_en: 'JEDZ manual = 30 min, here in 10 sec',
    action_route: 'demo',
    estimated_min: 1,
  },
  {
    key: 'mark_submitted',
    icon: MailCheck,
    title_pl: 'Oznacz ofertę jako złożoną',
    title_en: 'Mark offer as submitted',
    desc_pl: 'Po wgraniu do BZP — Adam śledzi czas do rozstrzygnięcia',
    desc_en: 'After uploading to BZP — Adam tracks award timing',
    benefit_pl: 'Auto-przejście do EVALUATING stage',
    benefit_en: 'Auto-advance to EVALUATING stage',
    action_route: 'demo',
    estimated_min: 1,
  },
  {
    key: 'record_outcome',
    icon: Award,
    title_pl: 'Wpisz wynik (won/lost)',
    title_en: 'Record outcome (won/lost)',
    desc_pl: 'Adam uczy się: gdzie wygrywasz, gdzie tracisz, dlaczego',
    desc_en: 'Adam learns: where you win, where you lose, why',
    benefit_pl: 'Learn loop — Twoja przewaga konkurencyjna',
    benefit_en: 'Learn loop — your competitive edge',
    action_route: 'demo',
    estimated_min: 1,
  },
];


// 2026-05-27 (T8/D onboarding-simplification): preferowany stan collapsed
// dla nowych userów — Mission Board ma być opcjonalny, nie ciągnąć wzroku
// pierwszy login. localStorage zapamiętuje user preference per browser.
function _readExpanded() {
  try {
    const v = localStorage.getItem('__maskil_mission_board_expanded');
    if (v === '1') return true;
    if (v === '0') return false;
  } catch {}
  return false;  // default collapsed dla nowych userów
}

export default function MissionBoard({ lang = 'pl' }) {
  const [state, setState] = useState(null);
  const [expanded, setExpandedRaw] = useState(_readExpanded);
  const [hintShown, setHintShown] = useState(false);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const location = useLocation();

  // Persist expanded preference + first-login hint
  const setExpanded = useCallback((next) => {
    setExpandedRaw(next);
    try {
      localStorage.setItem('__maskil_mission_board_expanded', next ? '1' : '0');
    } catch {}
  }, []);

  // Pierwszy login po wdrożeniu T8 — 1× toast hint że Mission Board JEST
  // opcjonalny. Sterowane przez localStorage flag żeby pokazać tylko raz.
  useEffect(() => {
    if (!state || hintShown) return;
    if (state.tutorial_dismissed || !state.welcome_seen) return;
    try {
      const seen = localStorage.getItem('__maskil_mb_hint_seen');
      if (seen === '1') return;
    } catch {}
    // Pokaż 2.5s po załadowaniu state (po WelcomeModal close)
    const id = setTimeout(() => {
      toast.info(
        t(lang,
          '💡 Mission Board (prawy dół) — opcjonalny przewodnik. Ignoruj jeśli nie potrzebujesz.',
          '💡 Mission Board (bottom-right) — optional guide. Ignore if not needed.'),
        { duration: 6000 }
      );
      try { localStorage.setItem('__maskil_mb_hint_seen', '1'); } catch {}
      setHintShown(true);
    }, 2500);
    return () => clearTimeout(id);
  }, [state, hintShown, lang]);

  const loadState = useCallback(async () => {
    try {
      const r = await api.get('/tutorial/state');
      setState(r.data);
    } catch (err) {
      console.error('tutorial state load:', err);
    }
    setLoading(false);
  }, []);

  useEffect(() => { loadState(); }, [loadState]);

  // Auto-refresh state every 60s — catches auto-completions from other pages
  useEffect(() => {
    const id = setInterval(loadState, 60000);
    return () => clearInterval(id);
  }, [loadState]);

  if (loading || !state) return null;

  // Don't show if dismissed or welcome not yet seen
  if (state.tutorial_dismissed) return null;
  if (!state.welcome_seen) return null;

  const completed = state.completed_missions || [];
  const skipped = state.skipped_missions || [];
  const totalCount = MISSIONS.length;
  const completedCount = MISSIONS.filter(m => completed.includes(m.key)).length;
  const skippedCount = MISSIONS.filter(m => skipped.includes(m.key)).length;
  const progressPct = Math.round((completedCount / totalCount) * 100);
  const isComplete = completedCount === totalCount;

  // Next mission to do (pomija completed + skipped)
  const nextMission = MISSIONS.find(m => !completed.includes(m.key) && !skipped.includes(m.key));

  // T8/D — per-mission skip
  async function handleSkipMission(missionKey) {
    try {
      await api.post(`/tutorial/missions/${missionKey}/skip`);
      // Optymistyczna aktualizacja state lokalnie
      setState((s) => ({
        ...s,
        skipped_missions: [...(s?.skipped_missions || []), missionKey],
      }));
    } catch (err) {
      console.error('skip mission failed:', err);
    }
  }

  async function handleDismiss() {
    if (!window.confirm(t(lang,
      'Ukryć Mission Board? Możesz wrócić: Settings → "Pokaż samouczek".',
      'Hide Mission Board? Resume: Settings → "Show tutorial".'))) return;
    try {
      await api.post('/tutorial/dismiss');
      setState({ ...state, tutorial_dismissed: true });
    } catch (err) { console.error(err); }
  }

  // Per-mission UX hint (toast + scroll target) gdy user JUŻ jest na docelowej stronie.
  // Fix 2026-05-26 (zgł. e638ae66 Izabela): navigate(sameRoute) = no-op
  // → user widzi „nic się nie dzieje". Dajemy konkretną wskazówkę.
  const MISSION_HINTS = {
    analyze_tender:    { msg: 'Jesteś już na stronie SWZ — kliknij przycisk „Analizuj NLP" w pasku akcji', target: 'btn-analyze' },
    extract_boq:       { msg: 'Otwórz Profit Room (zakładka po prawej) → sekcja BoQ', target: 'profit-room-link' },
    select_suppliers:  { msg: 'W Profit Room → sekcja „Dostawcy" → wybierz 3-5', target: 'suppliers-section' },
    send_rfq:          { msg: 'W Profit Room → sekcja RFQ → klik „Wyślij RFQ"', target: 'rfq-section' },
    view_quotes:       { msg: 'Quoty od dostawców pojawią się w Profit Room poniżej', target: 'quotes-section' },
    check_verdict:     { msg: 'Verdict + sensitivity są w Profit Room na dole', target: 'verdict-section' },
    generate_offer:    { msg: 'Kliknij „Generuj ofertę" w pasku akcji', target: 'btn-offer' },
    generate_jedz:     { msg: 'Kliknij „Generuj JEDZ" w pasku akcji', target: 'btn-jedz' },
    mark_submitted:    { msg: 'Kliknij „Oznacz jako złożone" w pasku akcji', target: 'btn-submitted' },
    record_outcome:    { msg: 'Kliknij „Wynik przetargu" w pasku akcji', target: 'btn-outcome' },
  };

  async function handleActionClick(mission) {
    // Mark as in-progress
    try {
      await api.post(`/tutorial/missions/${mission.key}/in-progress`).catch(() => null);
    } catch {}

    // Resolve route — special case for "demo" tokens
    let route = mission.action_route;
    if (route === 'demo') {
      route = state.demo_opp_id ? `/tenders/${state.demo_opp_id}` : '/tenders/TEST-LED-001';
    } else if (route === 'demo/profit-room') {
      const opp = state.demo_opp_id || 'TEST-LED-001';
      route = `/tenders/${opp}/profit-room`;
    }

    // Jeśli user JUŻ jest na docelowej ścieżce — navigate(sameRoute) nie remount'uje
    // i wizualnie wygląda jak no-op. Wyświetl toast z konkretną wskazówką + scroll.
    const currentPath = location.pathname;
    if (currentPath === route) {
      const hint = MISSION_HINTS[mission.key];
      if (hint) {
        toast.info(hint.msg, { duration: 6000 });
        // Próbuj scrollnąć do targetu jeśli istnieje na stronie
        setTimeout(() => {
          const el = document.querySelector(`[data-mission-target="${hint.target}"]`);
          if (el) {
            el.scrollIntoView({ behavior: 'smooth', block: 'center' });
            // Krótki highlight
            el.classList.add('ring-2', 'ring-primary', 'ring-offset-2');
            setTimeout(() => el.classList.remove('ring-2', 'ring-primary', 'ring-offset-2'), 3000);
          }
        }, 200);
      } else {
        toast.info('Jesteś już na właściwej stronie — wykonaj akcję wskazaną w misji');
      }
      return;
    }
    navigate(route);
  }

  // COLLAPSED VIEW — mini pill
  if (!expanded) {
    return (
      <button
        onClick={() => setExpanded(true)}
        className="fixed bottom-4 right-4 z-50 bg-card border border-border rounded-full shadow-lg
                   px-4 py-2 flex items-center gap-2 text-sm hover:bg-muted/30 transition-all"
        data-testid="mission-board-expand"
      >
        <Target className="w-4 h-4 text-primary" />
        <span className="font-medium">{completedCount}/{totalCount}</span>
        <div className="w-12 h-1 bg-muted rounded-full overflow-hidden">
          <div className="h-full bg-primary" style={{ width: `${progressPct}%` }} />
        </div>
        <ChevronUp className="w-3 h-3 text-muted-foreground" />
      </button>
    );
  }

  // EXPANDED VIEW — full panel
  return (
    <div className="fixed bottom-4 right-4 z-50 bg-card border border-border rounded-xl shadow-2xl w-[360px]
                    max-h-[calc(100vh-32px)] flex flex-col">
      {/* Header */}
      <div className="px-4 py-3 border-b border-border flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Target className="w-4 h-4 text-primary" />
          <h3 className="text-sm font-semibold" style={{ fontFamily: 'Space Grotesk' }}>
            {isComplete
              ? t(lang, '🎉 Ukończone!', '🎉 Done!')
              : t(lang, 'Pierwsza misja', 'First mission')}
          </h3>
          <Badge variant="outline" className="text-[10px]">
            {completedCount}/{totalCount}
          </Badge>
        </div>
        <div className="flex gap-1">
          <button onClick={() => setExpanded(false)}
                  className="p-1 hover:bg-muted rounded text-muted-foreground"
                  data-testid="mission-board-collapse">
            <ChevronDown className="w-3.5 h-3.5" />
          </button>
          <button onClick={handleDismiss}
                  className="p-1 hover:bg-muted rounded text-muted-foreground"
                  title={t(lang, 'Ukryj na stałe', 'Hide permanently')}
                  data-testid="mission-board-dismiss">
            <X className="w-3.5 h-3.5" />
          </button>
        </div>
      </div>

      {/* Progress bar */}
      <div className="px-4 pt-2 pb-1">
        <div className="h-1.5 bg-muted rounded-full overflow-hidden">
          <div className="h-full bg-gradient-to-r from-primary to-primary/70 transition-all duration-500"
               style={{ width: `${progressPct}%` }} />
        </div>
      </div>

      {/* Active mission (next) */}
      {!isComplete && nextMission && (
        <div className="px-4 py-3 bg-primary/5 border-b border-primary/20">
          <div className="flex items-start gap-3">
            <div className="w-9 h-9 rounded-lg bg-primary/15 flex items-center justify-center flex-shrink-0">
              <nextMission.icon className="w-4 h-4 text-primary" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-[10px] uppercase tracking-wider text-primary font-medium mb-0.5">
                {t(lang, 'TERAZ →', 'NEXT →')}
              </div>
              <div className="text-sm font-semibold leading-tight mb-1">
                {lang === 'pl' ? nextMission.title_pl : nextMission.title_en}
              </div>
              <div className="text-[11px] text-muted-foreground leading-snug mb-2">
                {lang === 'pl' ? nextMission.desc_pl : nextMission.desc_en}
              </div>
              {nextMission.benefit_pl && (
                <div className="inline-flex items-center gap-1 text-[10px] text-primary mb-2">
                  <Clock className="w-2.5 h-2.5" />
                  {lang === 'pl' ? nextMission.benefit_pl : nextMission.benefit_en}
                </div>
              )}
              <div className="flex gap-1.5">
                <Button size="sm" className="flex-1 h-7 text-xs"
                        onClick={() => handleActionClick(nextMission)}
                        data-testid={`mission-action-${nextMission.key}`}>
                  {t(lang, 'Pokaż jak →', 'Show how →')}
                </Button>
                <Button size="sm" variant="ghost" className="h-7 text-xs px-2"
                        onClick={() => handleSkipMission(nextMission.key)}
                        title={t(lang, 'Pomiń tę misję', 'Skip this mission')}
                        data-testid={`mission-skip-${nextMission.key}`}>
                  {t(lang, 'Pomiń', 'Skip')}
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {isComplete && (
        <div className="px-4 py-4 bg-emerald-500/10 border-b border-emerald-500/30 text-center">
          <Sparkles className="w-8 h-8 mx-auto text-emerald-500 mb-2" />
          <div className="text-sm font-semibold mb-1">
            {t(lang, 'Świetna robota!', 'Great job!')}
          </div>
          <div className="text-xs text-muted-foreground mb-3">
            {t(lang,
              'Znasz pełen MASKIL flow. Zacznij realny przetarg z listy /tenders.',
              'You know full MASKIL flow. Start real tender from /tenders.')}
          </div>
          <Button size="sm" onClick={() => navigate('/tenders?sort=match')} className="w-full h-7 text-xs">
            {t(lang, 'Idź do realnych przetargów →', 'Go to real tenders →')}
          </Button>
        </div>
      )}

      {/* All missions list (compact) */}
      <div className="overflow-y-auto flex-1">
        <div className="divide-y divide-border">
          {MISSIONS.map((m, i) => {
            const isDone = completed.includes(m.key);
            const isSkipped = skipped.includes(m.key);
            const isCurrent = !isDone && !isSkipped && m.key === nextMission?.key;
            const Icon = m.icon;
            const clickable = !isDone;  // T8/D: free pick — każda nie-ukończona klikalna
            return (
              <div
                key={m.key}
                onClick={clickable ? () => handleActionClick(m) : undefined}
                className={`px-4 py-2 flex items-center gap-2.5 text-[11px] group ${
                  isDone ? 'opacity-60'
                    : isSkipped ? 'opacity-40'
                    : isCurrent ? 'bg-primary/5'
                    : 'hover:bg-muted/40 cursor-pointer'
                }`}
                title={clickable ? t(lang, 'Klik = otwórz tę misję', 'Click = open this mission') : undefined}
              >
                <div className={`w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0 ${
                  isDone ? 'bg-emerald-500/20'
                    : isSkipped ? 'bg-muted'
                    : isCurrent ? 'bg-primary/20'
                    : 'bg-muted'
                }`}>
                  {isDone ? (
                    <Check className="w-3 h-3 text-emerald-500" />
                  ) : isSkipped ? (
                    <X className="w-3 h-3 text-muted-foreground" />
                  ) : isCurrent ? (
                    <span className="text-[10px] font-bold text-primary">{i + 1}</span>
                  ) : (
                    <Icon className="w-3 h-3 text-muted-foreground" />
                  )}
                </div>
                <div className={`flex-1 truncate ${isDone || isSkipped ? 'line-through' : ''}`}>
                  {lang === 'pl' ? m.title_pl : m.title_en}
                </div>
                <span className="text-muted-foreground text-[10px]">{m.estimated_min}min</span>
                {!isDone && !isSkipped && (
                  <button
                    onClick={(e) => { e.stopPropagation(); handleSkipMission(m.key); }}
                    className="opacity-0 group-hover:opacity-100 transition-opacity p-0.5 text-muted-foreground hover:text-foreground"
                    title={t(lang, 'Pomiń tę misję', 'Skip this mission')}
                  >
                    <X className="w-3 h-3" />
                  </button>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* Footer */}
      <div className="px-4 py-2 border-t border-border text-center">
        <div className="text-[10px] text-muted-foreground">
          {t(lang,
            `Łączny czas: ~${MISSIONS.reduce((sum, m) => sum + m.estimated_min, 0)} min`,
            `Total time: ~${MISSIONS.reduce((sum, m) => sum + m.estimated_min, 0)} min`)}
        </div>
      </div>
    </div>
  );
}
